import React, { useState, useCallback } from 'react';
import { toast } from 'react-toastify';

/**
 * Hook customizado para manejar la validación del formulario de evaluación
 * Centraliza toda la lógica de validación por pasos
 */
const useEvaluacionValidation = () => {
  const [errors, setErrors] = useState({});

  const normalizarTexto = (texto = '') =>
    texto
      .toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();

  const esTipoPruebaPsicologica = (formData, tiposPrueba = []) => {
    const valorSeleccionado = formData?.tipoPrueba;
    const tipoSeleccionado = tiposPrueba.find(tp => String(tp.value) === String(valorSeleccionado));
    const etiqueta = tipoSeleccionado?.label || tipoSeleccionado?.nombre || valorSeleccionado || '';
    return normalizarTexto(etiqueta).includes('psicolog');
  };

  const validateStep = useCallback((step, formData, context = {}) => {
    const newErrors = {};
    const tiposPrueba = context?.tiposPrueba || [];
    const requiereSubtipoPsicologico = esTipoPruebaPsicologica(formData, tiposPrueba);
    
    switch(step) {
      case 1:
        // Validaciones del Paso 1: Información Básica
        if (!formData.nombreEvaluacion?.trim()) {
          newErrors.nombreEvaluacion = 'El nombre de la evaluación es obligatorio';
        }
        if (!formData.tipoPrueba) {
          newErrors.tipoPrueba = 'Debe seleccionar un tipo de prueba';
        }
        if (requiereSubtipoPsicologico && !formData.subtipoPruebaPsicologica) {
          newErrors.subtipoPruebaPsicologica = 'Debe seleccionar una categoría psicológica';
        }
        if (!formData.idTipoCargo) {
          newErrors.idTipoCargo = 'Debe seleccionar un tipo de cargo';
        }
        
        // Validación: Al menos un cargo seleccionado
        if (!formData.cargosSeleccionados || formData.cargosSeleccionados.length === 0) {
          newErrors.cargosSeleccionados = 'Debe seleccionar al menos un cargo';
          toast.dismiss('editar-eval-cargo');
          toast.warning('Debe seleccionar al menos un cargo para continuar', { toastId: 'editar-eval-cargo', autoClose: 5000 });
        }
        break;
        
      case 2: {
        // Validaciones del Paso 2: Configuración
        if (!formData.tiempoLimite || formData.tiempoLimite <= 0) {
          newErrors.tiempoLimite = 'El tiempo límite debe ser mayor a 0 minutos';
        }
        break;
      }
        
      case 3:
      case 4: // Validar tambien en paso 4 antes de guardar
        // Validaciones del Paso 3: Preguntas
        if (!formData.preguntas || formData.preguntas.length === 0) {
          newErrors.preguntas = 'Debe agregar al menos una pregunta';
        }
        
        // Validación estricta de opciones vacías
        const preguntasIncompletas = [];
        if (formData.preguntas) {
          formData.preguntas.forEach((pregunta, index) => {
            if (pregunta.opciones) {
              pregunta.opciones.forEach((opcion, opIndex) => {
                if (!opcion.texto || opcion.texto.trim() === '') {
                  preguntasIncompletas.push(`Pregunta ${index + 1} - Opción ${opIndex + 1}`);
                }
              });
            }
          });
        }
        
        if (preguntasIncompletas.length > 0) {
          newErrors.opcionesVacias = true;
          toast.error(
            <div>
              <strong>Error de Validación:</strong><br/>
              Las siguientes opciones no tienen texto:<br/>
              <ul style={{ paddingLeft: '20px', marginTop: '5px', marginBottom: '0' }}>
                {preguntasIncompletas.slice(0, 3).map((msg, i) => <li key={i}>{msg}</li>)}
                {preguntasIncompletas.length > 3 && <li>... y {preguntasIncompletas.length - 3} más</li>}
              </ul>
            </div>
          );
        }

        if (step === 4) {
             // Validaciones finales adicionales antes del envío
            if (!formData.nombreEvaluacion?.trim()) {
                newErrors.nombreEvaluacion = 'El nombre de la evaluación es obligatorio';
            }
            if (!formData.tipoPrueba) {
                newErrors.tipoPrueba = 'Debe seleccionar un tipo de evaluación';
            }
            if (requiereSubtipoPsicologico && !formData.subtipoPruebaPsicologica) {
              newErrors.subtipoPruebaPsicologica = 'Debe seleccionar una categoría psicológica';
            }
            if (!formData.idTipoCargo) {
                newErrors.idTipoCargo = 'Debe seleccionar un tipo de cargo';
            }
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, []);

  const clearError = useCallback((field) => {
    setErrors(prev => ({
      ...prev,
      [field]: ''
    }));
  }, []);

  const clearAllErrors = useCallback(() => {
    setErrors({});
  }, []);

  return {
    errors,
    validateStep,
    clearError,
    clearAllErrors
  };
};

export default useEvaluacionValidation;